import { type StaticDecode, Type } from 'typebox'

import type {
    IdentityPersistenceCollectionDefinition,
    PersistenceCollection
} from '@tadpoles/persistence'

import { LibraryCatalogMetadata, type LibraryCategory } from '../../library/catalog.js'

type LibraryCatalogMetadataStorageRecord = StaticDecode<typeof LibraryCatalogMetadataStorageRecord>
const LibraryCatalogMetadataStorageRecord = Type.Object(
    {
        category: Type.String({ minLength: 1 }),
        ...LibraryCatalogMetadata.properties
    },
    { additionalProperties: false }
)

type LibraryCatalogMetadataCollection = PersistenceCollection<
    LibraryCatalogMetadataStorageRecord,
    'category'
>

export type LibraryCatalogMetadataPersistenceCollection = Pick<
    LibraryCatalogMetadataCollection,
    'get' | 'put'
>

export const LibraryCatalogMetadataCollectionDefinition: IdentityPersistenceCollectionDefinition<
    typeof LibraryCatalogMetadataStorageRecord,
    'category'
> = {
    kind: 'library-catalog-metadata',
    scope: 'global',
    primaryKey: 'category',
    modelSchema: LibraryCatalogMetadataStorageRecord
}

export class FamilyLibraryCatalogMetadataStore {
    readonly #collection: LibraryCatalogMetadataPersistenceCollection

    public constructor(collection: LibraryCatalogMetadataPersistenceCollection) {
        this.#collection = collection
    }

    public async load(
        category: LibraryCategory,
        signal: AbortSignal
    ): Promise<LibraryCatalogMetadata | null> {
        signal.throwIfAborted()
        const stored = await this.#collection.get(category)
        signal.throwIfAborted()

        if (stored === null) {
            return null
        }

        return { refreshTimestamp: stored.refreshTimestamp }
    }

    public async save(
        category: LibraryCategory,
        metadata: LibraryCatalogMetadata,
        signal: AbortSignal
    ): Promise<void> {
        signal.throwIfAborted()
        await this.#collection.put({ category, ...metadata })
    }
}
